import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <article className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer">
        <Link to={`/article/${article.id}`} className="flex flex-col sm:flex-row">
          <div className="w-full sm:w-48 h-40 sm:h-32 flex-shrink-0 overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 51cg吃瓜网`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="flex-1 p-4 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-600 font-medium whitespace-nowrap">
                {article.category}
              </span>
              {article.isHot && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                  热门
                </span>
              )}
            </div>
            <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1.5">
              {article.title}
            </h3>
            <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{article.summary}</p>
            <div className="flex items-center gap-3 text-xs text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group cursor-pointer">
        <Link to={`/article/${article.id}`} className="block">
          <div className="flex gap-3">
            <div className="w-20 h-16 flex-shrink-0 rounded-md overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 51cg吃瓜网`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                {article.title}
              </h4>
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer">
      <Link to={`/article/${article.id}`} className="block">
        <div className="relative overflow-hidden h-48">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 51cg吃瓜网`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {article.isHot && (
            <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap">
              热门
            </span>
          )}
          <span className="absolute top-3 right-3 px-2.5 py-0.5 rounded-full bg-background-950/60 backdrop-blur-sm text-background-50 text-xs font-medium whitespace-nowrap">
            {article.category}
          </span>
        </div>
        <div className="p-4">
          <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
          <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.summary}</p>
          <div className="flex items-center justify-between text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}